import { escapeHtml } from '@colplat/ui-web';
import webledgerManifest from '@colplat/app-webledger';
import {
  cascadeMembers, restorableEntities, summaryFieldsByEntity,
} from '@colplat/app-webledger/domain';
import { apiFetch, failureMessage } from '../api.js';
import { knownOffline } from '../offlineReads.js';
import { planRestore } from './webledgerOfflineState.js';
import {
  bootstrapWebledgerClient, clearPageNotice, pageFailure, webledgerShellHtml, wireBackButton,
  type WebledgerClient,
} from '../webledgerClient.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

/**
 * What has been deleted, and the way back.
 *
 * Every delete here is soft, so nothing on this page is recovery in the
 * forensic sense — the rows never went anywhere, and `field_history` still
 * holds every value they ever had. What was missing was a way to say so: the
 * client drops a deleted row from its local copy and a fresh sync never sends
 * it, so a deleted record simply stopped existing as far as the app went.
 *
 * Deliberately not fed from the local replica, which holds no deleted rows at
 * all and should not start: they would have to be excluded from every filter,
 * every total and every list, for ever, to be of use on one screen that is
 * read twice a year. This page asks the server, while it is open, and that is
 * the whole of its offline story.
 */

/**
 * Which entities this page offers, and how a row of each is described —
 * both from the manifest, through the same functions the server uses, so the
 * page cannot offer a category the server would refuse.
 */
const RESTORABLE: string[] = restorableEntities(webledgerManifest).map(([name]) => name);
const SUMMARY: Record<string, string[]> = summaryFieldsByEntity(webledgerManifest);

/** Plural for the picker; anything not here falls back to the entity's own name. */
const LABELS: Record<string, string> = {
  transaction: 'Transactions',
  account: 'Accounts',
  wallet: 'Wallets',
  tag: 'Tags',
  currency: 'Currencies',
  purpose: 'Purposes',
  budget: 'Budgets',
  template: 'Templates',
  templateFolder: 'Template folders',
  attachment: 'Attachments',
  icon: 'Icons',
  bankConnection: 'Bank connections',
  bankRule: 'Bank rules',
};

export async function mountWebledgerDeleted(root: HTMLElement): Promise<void> {
  root.innerHTML = webledgerShellHtml('deleted', bodyHtml());
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('#dlTabs')!);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;

  const page = new DeletedPage(root, client);
  await page.load();
  page.wireUndo();
}

class DeletedPage {
  private entity: string | undefined;
  /** `recordId -> who deleted it`, filled from the server when there is one. */
  private readonly authors = new Map<string, string>();

  private readonly token: string;
  private readonly notify: (message: string) => void;
  /** Announced *and* on screen — see `pageFailure`. */
  private readonly fail: (message: string) => void;

  constructor(
    private readonly root: HTMLElement,
    private readonly client: WebledgerClient,
  ) {
    this.token = client.token;
    this.notify = client.notify;
    this.fail = pageFailure(root, client.notify);
  }

  /**
   * Wired once, outside `load()`, because `load()` redraws this section on
   * every change — a listener added there would be added again each time and
   * one click would undo several deletes.
   */
  wireUndo(): void {
    this.el('#dlUnsent').addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-undo]');
      if (btn) void this.undo(btn.dataset['undo']!);
    });
    // The picker and the list are redrawn by `load()` and `show()`, so these
    // are delegated from the containers and bound once. Binding them inside
    // the redraw — which an earlier version did — adds a second handler each
    // time, and one click then restores several records.
    this.el('#dlTabs').addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-entity]');
      if (btn) this.show(btn.dataset['entity']!);
    });
    this.el('#dlRows').addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-restore]');
      if (btn) void this.restore(btn.dataset['restore']!, btn);
    });
  }

  private el<T extends HTMLElement>(id: string): T {
    return this.root.querySelector<T>(id)!;
  }

  async load(): Promise<void> {
    /*
     * Read from the local copy, not the server.
     *
     * Deleted rows travel to the device like any other (ADR-0025, amended
     * 2026-10-06), so this page needs no connection at all — which matters
     * most at the moment somebody wants it, having just deleted the wrong
     * thing on a train. The server is still the authority for the restore
     * itself; it is only the *listing* that is local.
     */
    const unsent = await this.unsentRows();
    this.el('#dlUnsent').innerHTML = unsentHtml(unsent);

    const counts = RESTORABLE
      .map((entity) => ({ entity, count: this.client.dataset.deletedRows(entity).length }))
      .filter((e) => e.count > 0);

    // Who deleted it is the one thing the local copy does not know: the row
    // carries `deleted_at`, but the author lives in `record_events`, which is
    // not replicated. Asked for when there is a connection and simply left
    // out when there is not — the list itself never waits on it.
    void this.loadAuthors(counts.map((c) => c.entity));

    if (counts.length === 0) {
      this.el('#dlTabs').innerHTML = '';
      this.el('#dlRows').innerHTML = unsent.length > 0
        ? '<p class="text-secondary mb-0">Nothing else has been deleted.</p>'
        : '<p class="text-secondary mb-0">Nothing has been deleted. This page lists anything that has,'
          + ' so it can be put back.</p>';
      return;
    }

    this.el('#dlTabs').innerHTML = counts.map((e, i) => `
      <button class="btn btn-sm ${i === 0 || e.entity === this.entity ? 'btn-primary' : 'btn-outline-secondary'}"
        type="button" data-entity="${escapeHtml(e.entity)}">
        ${escapeHtml(LABELS[e.entity] ?? e.entity)} <span class="badge text-bg-secondary ms-1">${e.count}</span>
      </button>`).join('');

    const chosen = counts.some((c) => c.entity === this.entity) ? this.entity! : counts[0]!.entity;
    this.show(chosen);
  }

  /**
   * Fill in "deleted by" from the server, then redraw if anything arrived.
   *
   * Deliberately not awaited by `load`: the page is complete without it, and
   * blocking a list that is already on the device behind a request would
   * give back exactly the delay that moving it local removed.
   */
  private async loadAuthors(entities: readonly string[]): Promise<void> {
    if (knownOffline()) return;
    let learned = false;
    for (const entity of entities) {
      const res = await apiFetch(`/apps/webledger/deleted/${encodeURIComponent(entity)}`, {
        token: this.token,
      }).catch(() => undefined);
      if (!res?.ok) return;   // offline after all, or refused — the list stands
      const body = await res.json() as { rows: Array<{ id: string; deletedBy: string | null }> };
      for (const row of body.rows) {
        if (row.deletedBy) { this.authors.set(row.id, row.deletedBy); learned = true; }
      }
    }
    if (learned && this.entity) this.show(this.entity);
  }

  private show(entity: string): void {
    this.entity = entity;
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>('#dlTabs button[data-entity]')) {
      const on = btn.dataset['entity'] === entity;
      btn.classList.toggle('btn-primary', on);
      btn.classList.toggle('btn-outline-secondary', !on);
    }
    this.el('#dlRows').innerHTML = rowsHtml(entity, this.client.dataset.deletedRows(entity), this.authors);
  }

  /**
   * Deletions made here that the server has not accepted yet.
   *
   * Read from the outbox and the local copy together: the queued delete says
   * which record, and the row is still in the dataset underneath the hiding,
   * which is what lets this say *what* was deleted rather than just listing
   * an id. Undoing one is dropping the queued entry — no request, so it works
   * with no connection, which is the whole point.
   */
  private async unsentRows(): Promise<Array<{ outboxId: string; recordId: string; label: string }>> {
    const queued = await this.client.outbox.allDocuments();
    return queued
      .filter((q) => q.op === 'delete' && !q.held)
      .map((q) => {
        const row = this.client.dataset.deletedLocally<{ id: string; description?: string }>(
          q.entity, q.recordId,
        );
        const text = row?.description?.trim();
        return {
          outboxId: q.id,
          recordId: q.recordId,
          label: text && text !== '' ? text : `${q.entity} ${q.recordId.slice(0, 8)}`,
        };
      });
  }

  /** Undo a delete that has not been sent: drop the queued entry, unhide the row. */
  private async undo(outboxId: string): Promise<void> {
    await this.client.outbox.removeDocument(outboxId);
    await this.client.refreshPendingWork();
    this.notify('Put back. The delete was never sent, so nothing goes to the server.');
    await this.load();
  }

  private async restore(id: string, btn: HTMLButtonElement): Promise<void> {
    clearPageNotice(this.root);
    btn.disabled = true;
    btn.textContent = 'Putting back…';
    const entity = this.entity!;

    /*
     * A delete that never left this device is undone by dropping it, not by
     * asking the server to reverse something it never heard about.
     */
    const plan = planRestore(await this.client.outbox.allDocuments(), id);
    if (plan.kind === 'cancel-unsent-delete') { await this.undo(plan.outboxId); return; }

    const send = async (): Promise<Response | undefined> => apiFetch(
      `/apps/webledger/deleted/${encodeURIComponent(entity)}/${encodeURIComponent(id)}/restore`,
      { method: 'POST', token: this.token },
    ).catch(() => undefined);

    const res = knownOffline() ? undefined : await send();

    // Offline, or online and unreachable: queue it. The record comes back
    // everywhere immediately, and the server is told when it can be.
    if (!res) {
      /*
       * The legs come back with it, not a minute later.
       *
       * Offline there is no "later" — nothing syncs until the connection
       * returns, so a transaction restored without its legs would sit there
       * showing no amounts for the whole session. `cascadeMembers` works out
       * the set from the rows the device already holds, using the shared
       * `deleted_at` that `softDeleteRecords` stamps across one operation.
       */
      const row = this.client.dataset.anyRow<{ id: string } & Record<string, unknown>>(entity, id);
      const members = typeof row?.['deleted_at'] === 'string'
        ? cascadeMembers(webledgerManifest, entity, id, row['deleted_at'],
          (child) => this.client.dataset.deletedRows<Record<string, unknown> & { id: string }>(child))
        : [{ entity, recordId: id }];

      await this.client.outbox.enqueueDocument({
        entity, recordId: id, op: 'restore', payload: { members },
        clock: this.client.session.nextClock(),
      });
      // Shown as present at once — the queued restore covers the record, and
      // the members cover its children, which `pendingRestores` alone would
      // not reach.
      await this.client.refreshPendingWork();
      await this.client.dataset.markRestored(members);
      this.notify(members.length > 1
        ? `Put back on this device, with ${members.length - 1} related record${
          members.length === 2 ? '' : 's'}. The server will be told when you are back online.`
        : 'Put back on this device. The server will be told when you are back online.');
      await this.load();
      return;
    }

    if (!res.ok) {
      btn.disabled = false;
      btn.textContent = 'Put back';
      this.fail(await failureMessage(res, 'Could not restore that.'));
      return;
    }
    const body = await res.json() as {
      restored: number; alreadyLive?: boolean;
      members?: Array<{ entity: string; recordId: string }>;
    };
    // Applied from the server's own list, so the record leaves this page and
    // returns to every other one immediately rather than when the next delta
    // catches up a minute later.
    if (body.members?.length) await this.client.dataset.markRestored(body.members);
    this.notify(body.alreadyLive
      ? 'That one is already back.'
      : `Restored — ${body.restored} record${body.restored === 1 ? '' : 's'}.`);
    await this.load();
  }
}

function unsentHtml(rows: ReadonlyArray<{ outboxId: string; recordId: string; label: string }>): string {
  if (rows.length === 0) return '';
  const items = rows.map((r) => `
    <tr>
      <td>
        <div>${escapeHtml(r.label)}</div>
        <div class="small text-secondary">Deleted on this device — not sent yet</div>
      </td>
      <td class="text-end align-middle">
        <button class="btn btn-sm btn-outline-primary" type="button"
          data-undo="${escapeHtml(r.outboxId)}">Put back</button>
      </td>
    </tr>`).join('');
  return `
    <div class="alert py-2 px-3 mb-3">
      <div class="cf-h mb-2">Waiting to be sent</div>
      <p class="small text-secondary mb-2">
        You deleted ${rows.length === 1 ? 'this' : 'these'} while offline. ${
  rows.length === 1 ? 'It is' : 'They are'} hidden here and on every other page, and the
        deletion goes to the server next time you are online. Until then you can put
        ${rows.length === 1 ? 'it' : 'them'} back with no connection at all.
      </p>
      <table class="table table-sm align-middle mb-0"><tbody>${items}</tbody></table>
    </div>`;
}

function rowsHtml(
  entity: string, rows: ReadonlyArray<Record<string, unknown>>, authors: ReadonlyMap<string, string>,
): string {
  if (rows.length === 0) return '<p class="text-secondary small mb-0">Nothing deleted here.</p>';
  const fields = SUMMARY[entity] ?? ['name', 'description'];
  const cells = rows.map((r) => {
    const label = fields
      .map((f) => r[f])
      .map((v) => (typeof v === 'string' ? v
        : typeof v === 'number' || typeof v === 'boolean' ? String(v)
        : ''))
      .filter((text) => text !== '')
      .map((text) => escapeHtml(text))
      .join(' · ');
    const when = typeof r['deleted_at'] === 'string'
      ? new Date(r['deleted_at'].replace(' ', 'T')).toLocaleString()
      : 'at some point';
    const who = authors.get(String(r['id']));
    return `
      <tr>
        <td>
          <div>${label || '<span class="text-secondary">(no description)</span>'}</div>
          <div class="small text-secondary">Deleted ${escapeHtml(when)}${
  who ? ` by ${escapeHtml(who)}` : ''}</div>
        </td>
        <td class="text-end align-middle">
          <button class="btn btn-sm btn-outline-primary" type="button"
            data-restore="${escapeHtml(String(r['id']))}">Put back</button>
        </td>
      </tr>`;
  }).join('');
  return `<div class="table-responsive"><table class="table table-sm align-middle mb-0"><tbody>${cells}</tbody></table></div>`;
}

function bodyHtml(): string {
  return `
    <div class="cf-card cf-card--bleed p-3">
      <div class="cf-h mb-2">Deleted records</div>
      <p class="text-secondary small">
        Nothing here is gone. Deleting only marks a record as removed, so anything on this
        page can be put back exactly as it was, with its whole history intact. Restoring
        brings back everything that one delete removed — a transaction's legs come back with
        it — and nothing that was removed separately.
      </p>
      <div id="dlUnsent"></div>
      <div class="d-flex flex-wrap gap-2 mb-3" id="dlTabs"></div>
      <div id="dlRows"><p class="text-secondary small mb-0">Loading…</p></div>
    </div>
    <div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}
